import { TopologyGraph } from '@braidlabs/console';
import type { DiscoveryEntry } from '@braidlabs/gateway';
import { Network } from 'lucide-react';
import { Card } from '@/components/ui/card.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { ThemeBridge } from '@/components/theme-bridge.tsx';
import { useDrafts } from '@/hooks.ts';
import { useAuthenticated } from '@/session.tsx';
import type { FragmentDraft } from '@/api.ts';

/**
 * The topology page: the OSS graph, over this org's **drafts**.
 *
 * Drafts rather than the published snapshot, because the question asked here is "what will this
 * change?" — and answering it from what is already live is a diagram of the past. `TopologyGraph`
 * takes `DiscoveryEntry[]`, so the drafts are projected into that shape here rather than the
 * dataplane exposing a discovery endpoint the console could read.
 */
export function TopologyPage() {
  const session = useAuthenticated();
  const drafts = useDrafts(session);

  return (
    <section className="grid gap-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Topology</h1>
        <p className="text-muted-foreground text-sm">
          Routes, fragments, and origins in the current draft — what the registry would look like
          if it were published now.
        </p>
      </header>

      {drafts.error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the draft registry</AlertTitle>
          <AlertDescription>{drafts.error}</AlertDescription>
        </Alert>
      ) : !drafts.items ? (
        <Skeleton className="h-64 w-full" />
      ) : drafts.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Network />}
            title="Nothing to draw yet"
            description="Add a fragment in the Registry tab and its routes and origin will appear here."
          />
        </Card>
      ) : (
        <Card className="p-4">
          <ThemeBridge>
            <TopologyGraph entries={drafts.items.map(toDiscoveryEntry)} />
          </ThemeBridge>
        </Card>
      )}
    </section>
  );
}

function toDiscoveryEntry(draft: FragmentDraft): DiscoveryEntry {
  const { manifest } = draft;
  return {
    id: manifest.id,
    title: manifest.title ?? manifest.id,
    adapter: manifest.adapter ?? 'compat',
    mount: `/__braid/frag/${manifest.id}`,
    bound: manifest.bound ?? true,
    // Every draft is loadable by definition: it is this org's own registry, seen by a member of
    // that org. The flag exists for gateways that filter a public listing by principal.
    loadable: true,
    ...(manifest.description ? { description: manifest.description } : {}),
    ...(manifest.tags ? { tags: manifest.tags } : {}),
    ...(manifest.pierce ? { pierce: manifest.pierce } : {}),
    ...(manifest.src ? { src: manifest.src } : {}),
    ...(typeof manifest.endpoint === 'string' ? { endpoint: manifest.endpoint } : {}),
  };
}
